"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import { RefreshCwIcon } from "lucide-react";
import type { ReactElement } from "react";

/**
 * Long-form status labels for the indicator tooltip + `aria-label`.
 * Duplicated from `UPLOAD_PROCESSING_STATUS_LABELS` in
 * `@workspace/services` because `@workspace/ui` sits below services
 * in the dep graph. Keep the two maps in sync.
 */
const STATUS_LABELS: Record<string, string> = {
  "awaiting-source": "Imported — original file pending transfer",
  pending: "Queued for processing",
  processing: "Processing document…",
  "extracting-text": "Extracting text…",
  "text-ready": "Enhancing document…",
  "chunks-ready": "Indexing for search…",
  failed: "Processing failed",
};

export type FileProcessingIndicatorProps = {
  status?: string | null;
  onRetry?: () => void;
  /** Class applied to the dot span (and retry button when no `retryClassName` is given). */
  className?: string;
  /** Class applied to the retry button when `status === "failed" && onRetry`. */
  retryClassName?: string;
};

/**
 * Renders a tooltip-wrapped status indicator for a file going through the
 * in-app Docling document-processing pipeline. Shared between chat
 * `MessageAttachment`, workspace file list, and custom skill file list.
 *
 * Returns `null` for `null | undefined | "completed"` so callers can render
 * unconditionally.
 *
 * Positioning is the consumer's responsibility — the chat path passes
 * absolute-positioning classes; list-row consumers render inline.
 */
export function FileProcessingIndicator({
  status,
  onRetry,
  className,
  retryClassName,
}: FileProcessingIndicatorProps): ReactElement | null {
  if (!status || status === "completed") {
    return null;
  }

  const label = STATUS_LABELS[status] ?? status;

  if (status === "failed" && onRetry) {
    const retryButton = (
      <button
        aria-label="Retry processing"
        className={cn(
          "flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground transition-colors hover:bg-destructive/80",
          retryClassName ?? className
        )}
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
        type="button"
      >
        <RefreshCwIcon className="size-3" />
      </button>
    );

    return (
      <Tooltip delay={200}>
        <TooltipTrigger render={retryButton} />
        <TooltipContent className="text-xs" side="top">
          {label} — click to retry
        </TooltipContent>
      </Tooltip>
    );
  }

  let dotColor: string;
  if (status === "failed") {
    dotColor = "bg-destructive";
  } else if (status === "awaiting-source") {
    // Parked (no bytes yet), not actively processing — calm, no pulse.
    dotColor = "bg-muted-foreground/50";
  } else {
    dotColor = "bg-primary animate-pulse";
  }

  const dot = (
    <span
      aria-label={label}
      className={cn(
        "inline-block size-2.5 shrink-0 rounded-full",
        dotColor,
        className
      )}
      role="img"
    />
  );

  return (
    <Tooltip delay={200}>
      <TooltipTrigger render={dot} />
      <TooltipContent className="text-xs" side="top">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
